// src/components/Profile.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector }    from "react-redux";
import { useNavigate }                  from "react-router-dom";
import { login }                        from "../../store/authSlice";
import {
  fetchUserProfile,
  changePassword,
  clearPasswordState,
} from "../../store/profileSlice";
import { ToastContainer, toast }        from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Styles/Common.css";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    user,
    loading: profileLoading,
    error: profileError,
    pwChanging,
    pwError,
    pwSuccess,
  } = useSelector((s) => s.profile);

  const { loading: loginLoading } = useSelector((s) => s.auth);

  // popup state
  const [showModal, setShowModal]     = useState(false);
  const [step, setStep]               = useState("verify"); // "verify" or "change"
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew]         = useState("");
  const [confirmNew, setConfirm]      = useState("");

  // on mount, load profile
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // handle global password-change responses
  useEffect(() => {
    if (pwSuccess) {
      toast.success("Password changed!");
      closeModal();
      dispatch(clearPasswordState());
    } else if (pwError) {
      const msg =
        typeof pwError === "string"
          ? pwError
          : pwError.detail
          ? Array.isArray(pwError.detail)
            ? pwError.detail.map((e) => e.msg || e).join("; ")
            : pwError.detail
          : JSON.stringify(pwError);
      toast.error(msg);
      dispatch(clearPasswordState());
    }
  }, [pwSuccess, pwError, dispatch]);

  if (profileLoading) return <p>Loading profile…</p>;
  if (profileError)   return <p className="error">{profileError}</p>;
  if (!user)          return <p>No profile data available.</p>;

  const openModal = () => {
    setStep("verify");
    setCurrent("");
    setNew("");
    setConfirm("");
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setStep("verify");
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      // reuse your login thunk just to verify current password
      await dispatch(
        login({
          username: user.username,
          password: currentPassword,
          role: user.role,
        })
      ).unwrap();
      setStep("change");
    } catch (err) {
      toast.error(err.detail || "Current password incorrect");
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (newPassword !== confirmNew) {
      toast.error("New passwords do not match");
      return;
    }
    dispatch(
      changePassword({
        email: user.email,
        newPassword,
      })
    );
  };

  return (
    <div className="container">
      <ToastContainer position="bottom-right" autoClose={3000} />

      <h2>
        My Profile
      </h2>

      <table className="table">
        <tbody>
          <tr>
            <th>Username</th>
            <td>{user.username}</td>
          </tr>
          <tr>
            <th>Employee Id</th>
            <td>{user.empId || "—"}</td>
          </tr>
          <tr>
            <th>Email</th>
            <td>{user.email}</td>
          </tr>
          <tr>
            <th>Role</th>
            <td>{user.role}</td>
          </tr>
        </tbody>
      </table>
      <hr/>
      <div>
        <h5><u>Manage Your Account</u></h5>
        <section>  
        <span onClick={openModal} className="profileIcon">Change Password&nbsp;
            <i className="fa fa-lock" title="Change password"/>
        </span>
        </section>
      </div>
      {showModal && (
        <div className="popup-overlay">
          <div className="popup-content" >
            <button
              className="close-btn fa fa-close"
              onClick={closeModal}
            />
            {step === "verify" ? (
              <>
                <h3>Verify Current Password</h3>
                <form onSubmit={handleVerify}>
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrent(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loginLoading}
                  >
                    {loginLoading ? "Verifying…" : "Verify"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h3>Set New Password</h3>
                <form onSubmit={handleChange}>
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNew(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmNew}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={pwChanging}
                  >
                    {pwChanging ? "Changing…" : "Change Password"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
